<template>
  <div class="lg:flex-row flex flex-col mt-10">
    <div class="lg:mr-10 lg:items-start flex flex-col items-center m-0">
      <nuxt-img
        :src="user.avatar.medium"
        alt=""
        srcset=""
        class="object-cover w-56 h-56 rounded-full"
        loading="lazy"
      />
      <span class="w-56 mt-6 text-xl font-black text-center">
        {{ user.publicName }}
      </span>

      <ConfirmedIcons
        :is-phone-confirmed="user.proofs.phoneConfirmed"
        :is-email-confirmed="user.proofs.emailConfirmed"
        :has-payment-method="user.proofs.hasPaymentMethod"
        :has-private-info="user.proofs.hasPrivateInfo"
        class="mx-auto my-6"
      />
      <RmBtn
        v-if="user.isFavorite"
        color="super-light"
        class="relative px-6 py-2 rounded-[12px]"
        @click="handleClickRemoveUserFromFavorites"
      >
        <HeartFillIcon class="md:ml-3 absolute left-0 w-6 h-6 ml-2" />
        <div class="ml-5">
          {{ $t('lots.id.userDescription.action.removeFromFavorites') }}
        </div>
      </RmBtn>
      <RmBtn
        v-else
        color="super-light"
        class="relative px-6 py-2 rounded-[12px]"
        @click="handleClickSaveUserInFavorites"
      >
        <HeartIcon class="md:ml-3 absolute left-0 w-6 h-6 ml-2" />
        <div class="ml-5">
          {{ $t('lots.id.userDescription.action.addToFavorites') }}
        </div>
      </RmBtn>
    </div>
    <div class="w-full">
      <div>
        <h2 class="mb-10 text-3xl font-semibold text-center">
          {{ user.publicName }} {{ $t('user.aboutSelf') }}
        </h2>
        <div class="whitespace-pre-line">
          <template v-for="(part, index) in processedDescription">
            <a
              v-if="part.isLink"
              :key="`link-${index}`"
              href="https://spb.crazymamas.ru/"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-500 hover:text-blue-700 underline"
            >{{ part.text }}</a><span
              v-else
              :key="`text-${index}`"
            >{{ part.text }}</span>
          </template>
        </div>
        <hr class="my-4" />
        <h2 class="mt-10 mb-10 text-3xl font-semibold text-center">
          {{ $t('user.authorLots') }}
        </h2>
      </div>
      <RmPagination
        v-if="pageInfo && pageInfo.totalPages > 1"
        :page-info="pageInfo"
        class="w-full"
      />
      <div class="lg:justify-start flex flex-wrap justify-center">
        <Card
          v-for="lot in lots"
          :key="lot.id"
          :lot="lot"
          :page-name="user.publicName"
          :page-type="'OWNER'"
          class="max-w-[230px] mr-4 mb-4"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUpdated } from 'vue'
import HeartFillIcon from '~/assets/svg/lot-page/heart-fill.svg?component'
import HeartIcon from '~/assets/svg/lot-page/heart.svg?component'
import Card from '~/components/lots/display_type/card.vue'
import RmBtn from '~/components/shared/rm_btn.vue'
import RmPagination from '~/components/shared/rm_pagination.vue'
import ConfirmedIcons from '~/components/user/verified/index.vue'
import { useUserDescription } from '~/composables/useUserDescription'
import { userQueries } from '~/services/queries'

const route = useRoute()
const { $gql, $tracking, $modal } = useNuxtApp()


const { isSignedIn } = useAuth()
const favoritUserAdd = useFavorites().favoritUserAdd
const favoritUserRemove = useFavorites().favoritUserRemove

const { data, pending  } = await useAsyncData(`user-${route.params.id}-${route.query.page}`, async () => {
  const page = parseInt(route.query.page) || 1
  const perPage = 28
  const { result } = await $gql.request(userQueries.getUser, {
    id: route.params.id,
    page,
    perPage,
  })
  return {
    result,
  }
}, {
  watch: [() => route.query],
})


watch(pending, newPending => {
  const indicator = useLoadingIndicator()
  if (newPending) {
    indicator.start({ force: true })
  } else {
    indicator.finish({ force: true })
  }
})


const user = computed(() => data.value.result)
const lots = computed(() => user.value.lots.collection)
const pageInfo = computed(() => user.value.lots.metadata)

const { processedDescription } = useUserDescription(user)

onMounted(() => {
  $tracking.logEventAmplitude('view owner page', {
    'page name': user.value.publicName,
    'page type': 'OWNER',
    'batch number': pageInfo.value.currentPage,
    'num of products': pageInfo.value.totalCount,
    'owner id': user.value.id,
    'owner city': lots.value?.[0]?.city.name,
    'owner rating': user.value.avgRating || '',
    'owner num of reviews': user.value.rentReviews.length,
    'owner search rank': user.value.searchRank,
  })
})

onUpdated(() => {
  amplitudeShowMore()
})

const amplitudeShowMore = () => {
  $tracking.logEventAmplitude('show more products', {
    'load method': 'PAGINATION',
    'batch number': pageInfo.value.currentPage,
    'num of products': pageInfo.value.totalCount,
    'owner id': user.value.id,
    'owner city': lots.value?.[0]?.city.name,
    'owner rating': user.value.avgRating || '',
    'owner num of reviews': user.value.rentReviews.length,
    'owner search rank': user.value.searchRank,
  })
}

const handleClickSaveUserInFavorites = async () => {
  if (!isSignedIn.value) {
    $modal.show('flow/auth')
  } else {
    const result = await favoritUserAdd(user.value.id)
    if (result) {
      data.value = {
        ...data.value,
        result: {
          ...data.value.result,
          isFavorite: true,
        },
      }
    }
  }
}

const handleClickRemoveUserFromFavorites = async () => {
  const result = await favoritUserRemove(user.value.id)
  if (result) {
    data.value = {
      ...data.value,
      result: {
        ...data.value.result,
        isFavorite: false,
      },
    }
  }
}
</script>
